/**
 * RFC 7807 ProblemDetails → a typed error.
 *
 * The API answers every failure with ProblemDetails and gives each status a
 * consistent meaning (docs/architecture.md §4.1). Preserving those
 * distinctions is what lets the UI react correctly instead of showing one generic
 * "something went wrong":
 *
 *   401  no token, an expired one, or a refresh token used as a credential
 *   403  authenticated, but not the owner / Game Master / Admin this needs
 *   404  no such row — INCLUDING an id that exists but belongs to someone else
 *   409  a rule the domain forbids (a second front cover, cancelling a finished job)
 *   400  malformed, or the domain service rejected the contents
 *
 * The 404-not-403 choice is deliberate server-side: sub-entities resolve from an
 * already-access-checked parent, so a caller cannot probe ids to learn what
 * exists. Which means a 404 here is "gone or never yours" — render it as an empty
 * state, never as an error.
 */

/** The wire shape. Extra members are allowed by the RFC and used by ASP.NET. */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Present on ValidationProblemDetails: field name → messages. */
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'bad-request'
  | 'server'
  | 'network'
  | 'unknown'

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly title: string | undefined
  readonly detail: string | undefined
  /** Field-keyed validation messages, when the server sent them. */
  readonly errors: Record<string, string[]> | undefined
  readonly problem: ProblemDetails | undefined

  constructor(init: {
    kind: ApiErrorKind
    status: number
    message: string
    title?: string | undefined
    detail?: string | undefined
    errors?: Record<string, string[]> | undefined
    problem?: ProblemDetails | undefined
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.kind = init.kind
    this.status = init.status
    this.title = init.title
    this.detail = init.detail
    this.errors = init.errors
    this.problem = init.problem
  }

  /** True for the statuses where retrying the same request cannot help. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  /** Flattens `errors` into a list, for forms that show a summary. */
  fieldMessages(): Array<{ field: string; message: string }> {
    if (this.errors === undefined) return []
    return Object.entries(this.errors).flatMap(([field, messages]) =>
      messages.map((message) => ({ field, message })),
    )
  }
}

export function kindForStatus(status: number, hasValidationErrors: boolean): ApiErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not-found'
  if (status === 409) return 'conflict'
  if (status === 400) return hasValidationErrors ? 'validation' : 'bad-request'
  if (status >= 500) return 'server'
  return 'unknown'
}

/**
 * Builds an ApiError from a failed response, reading ProblemDetails when the
 * server sent it and degrading gracefully when it did not — an HTML error page
 * from a proxy must not crash the parser and lose the status code.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetails | undefined
  try {
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('json')) {
      problem = (await response.json()) as ProblemDetails
    }
  } catch {
    problem = undefined
  }

  const errors = problem?.errors
  const hasValidationErrors = errors !== undefined && Object.keys(errors).length > 0
  const title = problem?.title
  const detail = problem?.detail

  return new ApiError({
    kind: kindForStatus(response.status, hasValidationErrors),
    status: response.status,
    message: detail ?? title ?? `Request failed with status ${response.status}`,
    title,
    detail,
    errors,
    problem,
  })
}
